import { useCallback, useMemo, useState } from 'react';

import { useSearchState } from '@fly/shared-hooks';
import { ColumnFiltersState } from '@tanstack/react-table';

import { TableStateType } from '../TanstackTable.types';

const SEARCH_PARAM_KEY = 'search';
export const serializeSearchState = (
  value: string | undefined,
  params: URLSearchParams
) => {
  if (value) {
    params.set(SEARCH_PARAM_KEY, value);
  } else {
    params.delete(SEARCH_PARAM_KEY);
  }
  return params;
};
const deserializeSearchState = (value: URLSearchParams) =>
  value.get(SEARCH_PARAM_KEY) || undefined;

export function useTableFilteringState(
  stateType: TableStateType,
  searchKey: string,
  onChange?: (filter: string) => void
) {
  const [searchState, setSearchState] = useSearchState<string | undefined>({
    initialValue: undefined,
    serialize: serializeSearchState,
    deserialize: deserializeSearchState,
  });

  const [stateFilter, setStateFilter] = useState<string | undefined>(undefined);

  const setFilter = useCallback(
    (data: string) => {
      onChange?.(data);

      if (stateType === 'search') {
        setSearchState(data);
        return;
      }

      setStateFilter(data);
    },
    [setSearchState, stateType, onChange]
  );

  const columnFilters: ColumnFiltersState | undefined = useMemo(() => {
    let filterValue: string = '';

    if (stateType === 'search') {
      filterValue = searchState || '';
    } else {
      filterValue = stateFilter || '';
    }

    return [{ id: searchKey, value: filterValue }];
  }, [stateType, stateFilter, searchKey, searchState]);

  return {
    columnFilters,
    setFilter,
  };
}
